import type { AllergensCopy } from '../types/allergens';

export const allergensIt: AllergensCopy = {
  seo: {
    title: 'Allergeni UE: i 14 e la legge, spiegati',
    description:
      'I 14 allergeni UE e cosa chiede la legge ai ristoranti, in parole semplici. Scopri come dichiarare gli allergeni sul tuo menu e le fonti nascoste che colgono di sorpresa le cucine.',
  },
  hero: {
    eyebrow: 'Guida pratica per ristoratori',
    headingPre: 'I 14 allergeni UE, spiegati in',
    headingScribble: 'parole semplici',
    body: 'Scopri quali allergeni copre la legge, dove si nascondono e come comunicarli chiaramente ai tuoi clienti. Questa guida ti accompagna sia tra i piatti sia tra le regole, senza legalese.',
    factAllergenCount: '14 allergeni',
    factRegulation: 'Regolamento (UE) n. 1169/2011',
    factReadTime: 'Lettura di 6 minuti',
    imageAlt: 'Un tavolo di ristorante con un menu QR SafestMenu',
  },
  intro: {
    leadPre: 'Se servi cibo nell’UE, indicare questi 14 allergeni è sia',
    leadScribble1: 'un obbligo di legge',
    leadMid: 'sia',
    leadScribble2: 'parte di una buona ospitalità',
    leadPost: '.',
    body: 'Questi allergeni si nascondono in più posti di quanti la maggior parte dei menu ammetta. Questa pagina spiega i 14 allergeni, la legge che li riguarda e come dichiararli con chiarezza - in modo semplice e pratico, così puoi agire subito.',
    stats: [
      { big: '14', small: 'sostanze in un elenco fisso' },
      { big: 'Ogni', small: 'ristorante, bar e caffè UE' },
      { big: 'Indicati', small: 'chiaramente, mai lasciati al caso' },
    ],
  },
  whyItMatters: {
    eyebrow: 'Perché è importante',
    heading: 'I clienti al sicuro sono quelli che tornano.',
    imageAlt: 'Clienti che guardano insieme un menu a un tavolo di ristorante',
    cards: [
      {
        title: 'Una reazione può essere grave',
        text: 'Per alcuni clienti, una traccia nascosta non è una preferenza - può mandarli in ospedale.',
      },
      {
        title: 'La fiducia è ospitalità',
        text: 'Se lo fai bene, le persone si rilassano. Informazioni chiare sugli allergeni fanno parte di un’accoglienza calorosa, non della burocrazia.',
      },
      {
        title: 'È la legge',
        text: 'Il ristorante è l’operatore del settore alimentare responsabile, quindi l’obbligo spetta a te, non al tuo fornitore.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'Il riferimento',
    heading: 'Scopri i quattordici',
    intro: 'Questi sono gli allergeni indicati nell’allegato II del regolamento (UE) n. 1169/2011. È un elenco fisso e chiuso - non scegli tu quali monitorare, e ogni piatto che ne contiene uno deve dichiararlo.',
    gotchas: {
      heading: '3 che colgono di sorpresa le cucine',
      items: [
        'L’olio di arachidi conta sempre come arachide, raffinato o no. È esente solo l’olio di soia completamente raffinato, quindi non estendere mai questa eccezione all’olio di arachidi.',
        'L’avena è naturalmente priva di glutine, ma quasi sempre contaminata durante la macinazione. Considera l’avena semplice come contenente glutine, a meno che non sia certificata senza glutine.',
        'La lecitina di soia (E322) è presente in gran parte di cioccolato, creme spalmabili e prodotti da forno, quindi controlla le schede dei fornitori, non solo gli ingredienti visibili.',
      ],
    },
  },
  legislation: {
    eyebrow: 'La legge, in breve',
    heading: 'Regolamento (UE) n. 1169/2011',
    paragraphs: [
      'Le regole derivano dal regolamento (UE) n. 1169/2011, spesso chiamato regolamento FIC (Food Information to Consumers). Essendo un regolamento UE, si applica direttamente in ogni stato membro, Romania compresa, senza bisogno di una versione nazionale.',
      'È in vigore dal 13 dicembre 2014. L’articolo 9 elenca le informazioni che il cibo deve riportare, l’articolo 21 riguarda il modo in cui gli allergeni vengono evidenziati sugli alimenti preimballati, e l’allegato II è l’elenco dei 14 allergeni stessi.',
      'Per il cibo servito sfuso - i piatti che prepari su ordinazione - l’articolo 44 rende obbligatoria l’informazione sugli allergeni, ma lascia a ogni paese la scelta del metodo. Anche le sanzioni sono stabilite a livello nazionale, secondo la legge sulla sicurezza alimentare di ciascun paese, non dal regolamento stesso.',
    ],
    asksHeading: 'Cosa richiede a te',
    asksItems: [
      'Indica i 14 allergeni che devi dichiarare',
      'Si applica sia ai menu sia alle etichette',
      'Copre sia il cibo preimballato sia quello sfuso servito',
      'Deve essere accurata e sempre aggiornata',
    ],
    natashasLaw:
      'Una norma di cui potresti aver sentito parlare, la Natasha’s Law del Regno Unito, è una regola britannica del 2021 - più severa della base UE, e non fa affatto parte del diritto dell’UE.',
  },
  steps: {
    eyebrow: 'Come dichiararli',
    heading: 'Trasforma l’obbligo in una routine semplice',
    intro: 'Questi 4 passaggi ti portano da una pila di ricette a informazioni sugli allergeni di cui un cliente può fidarsi.',
    items: [
      {
        title: 'Crea un’unica fonte di verità',
        text: 'Segna quali dei 14 allergeni contiene ogni piatto, ricavandoli dalle tue ricette e dalle schede dei fornitori. Tienili in un unico posto, così cucina e sala sono sempre allineate.',
      },
      {
        title: 'Mettila dove i clienti la vedono',
        text: 'Mostrala per iscritto come impostazione predefinita: sul menu, su una tabella chiara o su un menu digitale. Lo scritto batte sempre l’affidarsi alla memoria di qualcuno.',
      },
      {
        title: 'Forma il tuo team',
        text: 'Chi lavora in sala dovrebbe leggere dal registro e sapere quando verificare con la cucina invece di indovinare. Risposte precise, ogni volta.',
      },
      {
        title: 'Tienila aggiornata',
        text: 'Ogni modifica a una ricetta o un nuovo fornitore può aggiungere o togliere un allergene. Aggiorna il registro non appena qualcosa cambia.',
      },
    ],
    footnote: 'Ricontrolla ogni volta che una ricetta o un fornitore cambia - è lì che la maggior parte dei menu diventa obsoleta senza che nessuno se ne accorga.',
  },
  comparison: {
    eyebrow: 'Dove va l’informazione',
    heading: 'Cibo preimballato vs cibo sfuso',
    intro: 'Gli stessi 14 allergeni, dichiarati in posti diversi a seconda di come viene venduto il cibo. È la confusione che vediamo più spesso.',
    leftTitle: 'Cibo preimballato e confezionato',
    leftPoints: [
      'Venduto nella confezione in cui è stato messo prima di essere ordinato, come una salsa in bottiglia.',
      'Richiede un elenco ingredienti completo, con i 14 allergeni evidenziati al suo interno (articolo 21).',
      'Riporta anche gli altri dettagli in etichetta: denominazione, quantità e termine di conservazione.',
    ],
    rightTitle: 'Cibo servito sfuso',
    rightPoints: [
      'I piatti che prepari e servi su ordinazione, più il cibo confezionato sul posto su richiesta del cliente.',
      'Non serve un elenco ingredienti stampato, ma l’informazione sugli allergeni è obbligatoria (articolo 44).',
      'Fornita nel modo previsto dal tuo paese, ma sempre accurata e facile da ottenere prima di ordinare.',
    ],
  },
  crossContact: {
    eyebrow: 'In cucina',
    heading: 'Contaminazione crociata e "può contenere"',
    imageAlt: 'Un piano di lavoro del ristorante pulito e appena disinfettato',
    bodyPre:
      'Dichiarare gli allergeni che sono ingredienti è un compito. La contaminazione crociata è un altro - un allergene che si sposta per errore tra friggitrici, taglieri o coltelli condivisi. Una',
    bodyScribble: 'traccia è sufficiente',
    bodyPost:
      ', quindi "può contenere" è onesto solo dopo aver valutato il rischio reale e non essere riusciti a eliminarlo.',
    tiles: [
      'Piani e utensili separati',
      'Pulizia tra un ordine e l’altro',
      'Forma tutto il team',
      'Sii onesto con il "può contenere"',
    ],
  },
  digital: {
    eyebrow: 'Dove un menu QR aiuta',
    heading: 'Una modifica, aggiornato su ogni tavolo',
    body: 'I menu stampati e le tabelle plastificate condividono un punto debole: tenerli aggiornati. Un menu digitale elimina questo problema. Contrassegni ogni piatto una volta sola, e una modifica raggiunge subito ogni cliente - senza ristampe.',
    list: [
      'Contrassegna i 14 allergeni per piatto, una sola volta',
      'I clienti filtrano il menu in base a ciò che è sicuro per loro',
      'Sempre la versione aggiornata, nella loro lingua',
    ],
    footnote: 'Onesti sui limiti: un menu QR non rende corretti i tuoi dati né sostituisce la formazione del personale. Risolve le informazioni obsolete, che è la cosa più comune che va storta.',
    ctaLabel: 'Scopri come SafestMenu gestisce gli allergeni',
    qrImageAlt: 'Un cartoncino da tavolo con codice QR SafestMenu accanto a un caffè',
    dishImageAlt: 'Un piatto impiattato mostrato su un menu digitale SafestMenu',
    journeyEyebrow: 'Il percorso dell’informazione sugli allergeni',
    journeyIntro: 'L’informazione sugli allergeni è un flusso con 4 passaggi di consegna. Il tuo menu - e uno strumento come SafestMenu - si trova nel punto in cui raggiunge il cliente.',
  },
  faq: {
    eyebrow: 'Domande frequenti',
    heading: 'Risposte rapide',
    items: [
      {
        q: 'Quanti allergeni devo dichiarare?',
        a: 'Tutti e 14. La legge UE fissa un elenco chiuso nell’allegato II del regolamento 1169/2011, e ogni piatto che ne contiene uno deve dichiararlo. Puoi segnalare anche altre cose su cui i clienti chiedono, ma i 14 sono il minimo di legge.',
      },
      {
        q: 'Posso semplicemente dire ai clienti di chiedere al personale?',
        a: 'Dipende dal tuo paese. Alcuni stati membri permettono l’informazione sugli allergeni su richiesta, se un cartello chiaro invita i clienti a chiedere e il personale dà risposte accurate. Altri, come la Francia, la richiedono per iscritto. Scriverla è la scelta più sicura ovunque.',
      },
      {
        q: 'La Natasha’s Law si applica al mio ristorante?',
        a: 'Solo se ti trovi nel Regno Unito. La Natasha’s Law è una norma britannica dell’1° ottobre 2021 per il cibo confezionato sul posto prima che il cliente lo ordini, ed è più severa della base UE. Non si applica in Romania né nel resto dell’UE.',
      },
      {
        q: 'L’olio di arachidi è esente dalla dichiarazione?',
        a: 'No. L’olio di arachidi conta come arachide, raffinato o no, quindi va sempre dichiarato. Solo l’olio di soia completamente raffinato ha questa esenzione, e "olio di arachide" e "olio di arachis" sono solo altri nomi per l’olio di arachidi.',
      },
      {
        q: 'Chi è responsabile se l’informazione sugli allergeni è sbagliata?',
        a: 'Tu. Il ristorante è l’operatore del settore alimentare, quindi l’obbligo di fornire informazioni accurate spetta a te, anche se un fornitore ha cambiato una ricetta senza avvisarti. Per questo un unico registro aggiornato è così importante.',
      },
      {
        q: 'Un menu QR mi rende conforme da solo?',
        a: 'No, e non lo affermeremmo mai. Un menu digitale mantiene aggiornata l’informazione sugli allergeni su ogni tavolo contemporaneamente, ma i dati valgono quanto ciò che inserisci. Hai comunque bisogno di personale formato e di un vero processo contro la contaminazione crociata in cucina.',
      },
    ],
  },
  closingNote: {
    heading: 'Una nota su questa guida',
    body: 'Questa è una guida generale per aiutarti a organizzarti, non una consulenza legale. Le norme nazionali previste dall’articolo 44 variano da un paese UE all’altro e vengono aggiornate, quindi controlla le regole del luogo in cui operi o rivolgiti alla tua autorità nazionale per la sicurezza alimentare.',
  },
  emailCapture: {
    heading: 'Tieni questa guida a portata di mano',
    title: 'Ricevi la checklist degli allergeni via email',
    description: 'Ti invieremo questa guida più qualche nota pratica ogni tanto su menu ed etichettatura degli allergeni. Niente spam, puoi disiscriverti quando vuoi.',
    buttonLabel: 'Inviamela',
  },
  cta: {
    heading: 'Metti i tuoi allergeni su un menu di cui i clienti possono fidarsi',
    body: 'Contrassegna i 14 allergeni per piatto, aggiorna una volta sola, e ogni cliente vede la modifica al tavolo. Tieni un unico registro sempre aggiornato, e i clienti filtrano il menu in base a ciò che è sicuro per loro.',
    buttonLabel: 'Prova SafestMenu gratis',
  },
  diagrams: {
    journey: [
      { title: 'Il piatto', sub: 'Cosa contiene davvero, fino alla salsa' },
      { title: 'Il registro', sub: 'Ogni piatto mappato sui 14 allergeni' },
      { title: 'Il menu', sub: 'Mostrato al cliente, su carta o schermo', tag: 'SafestMenu' },
      { title: 'Il cliente', sub: 'Fa una scelta di cui può fidarsi' },
    ],
    routes: [{ label: 'Sul menu' }, { label: 'Su un cartello' }, { label: 'Detto su richiesta' }],
    orLabel: 'o',
    ingredients: {
      label: 'Ingredienti',
      segments: [
        { text: 'Farina di grano', mark: true },
        { text: ', acqua, olio di colza, ' },
        { text: 'uovo', mark: true },
        { text: ', zucchero, ' },
        { text: 'latte', mark: true },
        { text: ', sale, malto d’orzo.' },
      ],
      note: 'Allergeni evidenziati all’interno dell’elenco ingredienti.',
    },
  },
  reference: {
    ariaLabel: 'I 14 allergeni UE',
    allergenLabel: 'Allergene',
    commonlyIn: 'Comune in',
    hidesIn: 'Si nasconde in',
    remember: 'Ricorda',
    footnotePre: 'Tocca uno dei ',
    footnotePost: ' per scoprire dove si nasconde. Gli esempi sono un punto di partenza - controlla sempre le tue ricette e le schede tecniche dei fornitori.',
  },
  items: {
    'cereals-gluten': {
      name: 'Glutine',
      short: 'Grano, segale, orzo, avena, farro',
      common: 'Pane, pasta, impasto per pizza, torte, pasticceria, pastelle, pangrattato, cous cous, birra e la maggior parte dei sughi e delle salse addensate con farina.',
      hidden: 'Salsa di soia (di solito fermentata con grano), dadi da brodo e granulati per sughi, aceto di malto, alcune salsicce e gelati (con fette biscottate o farina di grano come riempitivo) e miscele di spezie.',
      note: 'L’avena è naturalmente priva di glutine, ma quasi sempre contaminata durante la macinazione. Non considerare un piatto a base di avena semplice sicuro per i celiaci, a meno che non sia certificato senza glutine.',
    },
    crustaceans: {
      name: 'Crostacei',
      short: 'Gamberi, granchio, aragosta, scampi',
      common: 'Gamberi, gamberetti, granchio, aragosta, scampi, gamberi di fiume e mazzancolle.',
      hidden: 'Pasta di gamberetti (belacan) nei curry thailandesi e del sud-est asiatico, brodo di pesce in zuppe e risotti, basi per bouillabaisse e paella, e cracker di gamberetti.',
      note: 'Una traccia di brodo di gamberi in una base di curry "vegetale" basta a scatenare una reazione grave. Controlla wok, friggitrici e pentole per brodo condivisi.',
    },
    eggs: {
      name: 'Uovo',
      short: 'Maionese, torte, glasse, crema pasticcera',
      common: 'Maionese, quiche, omelette, torte, meringa, pasta fresca all’uovo, crema pasticcera e alimenti in pastella o impanati.',
      hidden: 'Glassa d’uovo spennellata su pasticceria e pane, consommé chiarificati con albume, mousse, marshmallow e schiume decorative sui cocktail.',
      note: 'La doratura all’uovo sui prodotti da forno è facile da non notare leggendo un menu di sfuggita. Segnalala invece di affidarti alla descrizione della pasticceria.',
    },
    fish: {
      name: 'Pesce',
      short: 'Filetti, acciughe, salse',
      common: 'Filetti di pesce, bastoncini di pesce, sushi, torta di pesce, salmone affumicato e acciughe.',
      hidden: 'Salsa Worcestershire e condimento Caesar (acciughe), salsa di pesce nei piatti thailandesi e vietnamiti, alcune salse per pizza e pasta, miscele di salsa di ostriche e dadi da brodo.',
      note: 'La salsa di pesce e la salsa Worcestershire sono le due classiche fonti invisibili di pesce che si infilano in piatti altrimenti di carne o verdure.',
    },
    peanuts: {
      name: 'Arachidi',
      short: 'Nocciolina americana, satay, olio di arachide',
      common: 'Burro di arachidi, salsa satay, arachidi tostate e salate, olio di arachide e molti piatti asiatici e africani.',
      hidden: 'Salse al peperoncino e al curry arricchite con arachidi macinate, cioccolato e dessert prodotti su linee condivise, e proteine vegetali idrolizzate.',
      note: '"Olio di arachide" e "olio di arachis" sono solo altri nomi per l’olio di arachidi, ed entrambi vanno dichiarati. L’olio di arachidi raffinato non è esente - lo è solo l’olio di soia completamente raffinato.',
    },
    soybeans: {
      name: 'Soia',
      short: 'Salsa di soia, tofu, lecitina',
      common: 'Salsa di soia, tofu, edamame, miso, latte di soia e tempeh.',
      hidden: 'Molte salse e marinature asiatiche (hoisin, teriyaki), miscele di oli vegetali, carni lavorate con riempitivo di soia, alcuni pani e la lecitina di soia (E322).',
      note: 'La lecitina di soia (E322) è presente in gran parte di cioccolato, creme spalmabili e prodotti da forno. Controlla le schede tecniche dei fornitori, non solo l’ingrediente visibile.',
    },
    milk: {
      name: 'Latte',
      short: 'Formaggio, burro, panna, siero di latte',
      common: 'Latte, formaggio, burro, panna, yogurt e gelato.',
      hidden: 'Latte in polvere in alcuni pani e impasti, leganti a base di proteine del latte nelle carni lavorate, condimento al siero di latte sulle patatine, caseinato di sodio nei topping "senza latte" e salse a base di roux.',
      note: 'La proteina del latte (caseina o siero) compare in prodotti etichettati "senza latte" e nel pane. Non dare mai per scontato che pane semplice o una miscela di spezie secche siano senza latte senza controllare.',
    },
    'tree-nuts': {
      name: 'Frutta a guscio',
      short: 'Mandorla, nocciola, noce, anacardo',
      common: 'Mandorle, nocciole, noci, anacardi, noci pecan, noci del Brasile, pistacchi e macadamia - in torte, pesto, oli di frutta a guscio, latte di frutta a guscio e pralina.',
      hidden: 'Pesto e condimenti per insalata, oli di frutta a guscio usati per friggere o rifinire, korma e altre salse addensate con frutta a guscio macinata, marzapane e frangipane, granola e liquori alla frutta a guscio.',
      note: 'I latte macchiato con latte di mandorla e la pasta al pesto sono due errori facili da fare. Entrambi nascondono la frutta a guscio dietro un nome che non dice "frutta a guscio".',
    },
    celery: {
      name: 'Sedano',
      short: 'Sedano rapa, brodo, condimenti',
      common: 'Gambi di sedano, sedano rapa, zuppe, stufati e insalate.',
      hidden: 'Dadi da brodo e brodo (il sale di sedano è una base comune), granulati per sughi, miscele di spezie e sali aromatizzati, alcune salsicce e salse già pronte.',
      note: 'Il sale di sedano si nasconde in quasi ogni dado da brodo commerciale e miscela di condimenti. Controlla il brodo di base prima di dichiarare una zuppa o un sugo senza sedano.',
    },
    mustard: {
      name: 'Senape',
      short: 'Condimento, salse, marinature secche',
      common: 'Senape come condimento, senape in polvere, condimenti per insalata e piccalilli.',
      hidden: 'Maionese e molte salse confezionate, marinature e miscele di spezie per barbecue, curry in polvere e miscele di spezie, alcuni pani e carni lavorate.',
      note: 'La senape è un ingrediente nascosto nella maionese e nelle marinature. Raramente compare nel nome del piatto, ma è molto spesso nella salsa.',
    },
    sesame: {
      name: 'Sesamo',
      short: 'Panini, tahina, hummus',
      common: 'Semi di sesamo su pane e panini, tahina, hummus, halva, olio di sesamo e falafel.',
      hidden: 'Semi cotti sul fondo dei panini, miscele di spezie (za’atar, dukkah), condimenti a base di tahina e friggitrici condivise con prodotti impanati al sesamo.',
      note: 'Il sesamo è spesso cotto sul fondo del panino, non solo sparso sopra. Un’occhiata veloce al pane può non notarlo.',
    },
    sulphites: {
      name: 'Anidride solforosa e solfiti',
      short: 'Vino, frutta secca, aceto',
      common: 'Vino (soprattutto bianco e rosé), frutta secca come albicocche e uvetta, sciroppi, succhi di frutta e sidro.',
      hidden: 'Carni lavorate e salsicce, verdure sott’aceto e conservate, alcuni prodotti a base di patate tagliate e surgelate, salse a base di aceto e alcune bibite.',
      note: 'La soglia di legge è una concentrazione superiore a 10 mg/kg o 10 mg/l, non la semplice presenza. Le albicocche secche e il vino economico sono le due fonti più dimenticate.',
    },
    lupin: {
      name: 'Lupino',
      short: 'Farina di lupino, miscele "senza"',
      common: 'Farina di lupino e semi di lupino (lupini), oltre ad alcuni pani e prodotti da forno senza glutine.',
      hidden: 'Miscele di farina di grano e preparati da forno che usano il lupino come integratore proteico, alcune paste, sostituti vegani della carne e pani e paste da forno di tipo continentale.',
      note: 'Il lupino è raro nella cucina tradizionale britannica ma comune nei pani continentali e nelle miscele di farina senza glutine. Controlla ogni farina importata o etichettata "senza".',
    },
    molluscs: {
      name: 'Molluschi',
      short: 'Cozze, ostriche, calamari, lumache',
      common: 'Cozze, vongole, ostriche, capesante, calamari, polpo e lumache (escargot).',
      hidden: 'Salsa di ostriche in saltati e marinature, brodo di mare in basi per paella e risotto, inchiostro di calamaro usato per colorare pasta o riso, e surimi come estensori di pesce.',
      note: 'La salsa di ostriche è il classico mollusco nascosto. Compare in saltati e marinature che altrimenti sembrano solo di carne o verdure.',
    },
  },
};
